import React from 'react';
import styled from 'styled-components';
import DatePicker from 'react-date-picker';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { Body5, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { DATE_RANGE_PRESETS, DATE_RANGE_PRESET_LABELS } from '@utils/documentVault';
import { createTimeInstance } from '@utils/time';
import { DocumentVaultFilterContext } from '@/contexts/DocumentVaultFilterContext';

const Wrapper = styled.div`
  position: relative;
`;

const TriggerWrapper = styled.div`
  button {
    padding: 6px 12px;
  }
`;

const MenuWrapper = styled.div`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  min-width: 260px;

  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.appBackground};
  box-shadow: 0 2px 6px 1px rgba(0, 0, 0, 0.12);
  padding: 8px 0;
  z-index: 10000;
`;

const OptionRow = styled.div`
  padding: 8px 16px;
  cursor: pointer;

  background: ${({ theme, $isSelected }) => ($isSelected ? theme.themeColors.lowContrastBorder : 'transparent')};

  &:hover {
    background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  }
`;

const CustomRangeWrapper = styled.div`
  padding: 8px 16px 12px 16px;

  .react-date-picker__wrapper {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    border-radius: 4px;
    padding: 4px 6px;
  }

  .react-calendar {
    background: ${({ theme }) => theme.themeColors.appBackground};
    color: ${({ theme }) => theme.themeColors.text};
  }
`;

const PRESET_ORDER = [
  DATE_RANGE_PRESETS.all,
  DATE_RANGE_PRESETS.last7,
  DATE_RANGE_PRESETS.last30,
  DATE_RANGE_PRESETS.qtd,
  DATE_RANGE_PRESETS.ytd,
  DATE_RANGE_PRESETS.custom,
];

function toDateValue(value) {
  if (!value) return null;
  const instance = createTimeInstance(value);
  return instance ? instance.toDate() : null;
}

function toIsoDate(date) {
  if (!date) return null;
  return createTimeInstance(date).format('YYYY-MM-DD');
}

export const DateRangeFilter = () => {
  const { filters, setDateRange } = React.useContext(DocumentVaultFilterContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const [coords, setCoords] = React.useState({ top: 0, left: 0 });
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const { preset, from, to } = filters.dateRange;
  const isCustom = preset === DATE_RANGE_PRESETS.custom;

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.left });
    setIsOpen(true);
  };

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handleMouseDown = (event) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      // The calendar popup renders outside the menu element, so a click inside it would
      // otherwise read as a click-away and close everything mid-selection.
      const inCalendar = event.target.closest?.('.react-calendar');
      if (!inTrigger && !inMenu && !inCalendar) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handlePresetClick = (nextPreset) => {
    if (nextPreset === DATE_RANGE_PRESETS.custom) {
      setDateRange({ preset: nextPreset, from, to });
      return;
    }
    setDateRange({ preset: nextPreset, from: null, to: null });
    setIsOpen(false);
  };

  const label = preset === DATE_RANGE_PRESETS.all ? 'Date' : DATE_RANGE_PRESET_LABELS[preset];

  return (
    <Wrapper>
      <TriggerWrapper ref={triggerRef}>
        <SkeletonButton onClick={handleToggle}>{label}</SkeletonButton>
      </TriggerWrapper>
      {isOpen ? (
        <MenuWrapper ref={menuRef} $top={coords.top} $left={coords.left}>
          {PRESET_ORDER.map((value) => (
            <OptionRow key={value} $isSelected={preset === value} onClick={() => handlePresetClick(value)}>
              <Body5>{DATE_RANGE_PRESET_LABELS[value]}</Body5>
            </OptionRow>
          ))}
          {isCustom ? (
            <CustomRangeWrapper>
              <Container top={8}>
                <Body5 isLowContrast>From</Body5>
                <Container top={4}>
                  <DatePicker
                    value={toDateValue(from)}
                    onChange={(date) => setDateRange({ preset: DATE_RANGE_PRESETS.custom, from: toIsoDate(date), to })}
                    format="y-MM-dd"
                  />
                </Container>
              </Container>
              <Container top={12}>
                <Body5 isLowContrast>To</Body5>
                <Container top={4}>
                  <DatePicker
                    value={toDateValue(to)}
                    onChange={(date) => setDateRange({ preset: DATE_RANGE_PRESETS.custom, from, to: toIsoDate(date) })}
                    format="y-MM-dd"
                  />
                </Container>
              </Container>
            </CustomRangeWrapper>
          ) : null}
        </MenuWrapper>
      ) : null}
    </Wrapper>
  );
};
